import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  Badge,
  Button,
  Card,
  Form,
  Image,
  Modal,
  Placeholder,
  Spinner,
  Stack,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasAnyPermission, PERMISSION_IDS } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import '../../styles/admin.css';
import './AdminCourseDetail.css';
import { log, logError } from '../../utils/logger';

const FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="240"><rect width="400" height="240" rx="24" fill="%23eef2ff"/><g font-family="Open Sans" font-size="32" font-weight="600" fill="%2364768b"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">Curso sin imagen</text></g></svg>';

const sanitiseImage = (source) => {
  if (!source) return null;
  const clean = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(clean)) return clean;
  const base = getApiBaseUrl();
  if (clean.startsWith('/')) return `${base}${clean}`;
  if (clean.startsWith('uploads/')) return `${base}/${clean}`;
  return `${base}/uploads/${clean}`;
};

const minutesToDisplay = (minutes = 0) => {
  const total = Number(minutes) || 0;
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

const ensureArray = (input) => {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  return [];
};

const ModuleSkeleton = () => (
  <div className="ACD-skeleton-module">
    <div className="ACD-skeleton-header">
      <div className="ACD-skeleton-badge"></div>
      <div className="ACD-skeleton-title"></div>
    </div>
    <div className="ACD-skeleton-content">
      <div className="ACD-skeleton-line ACD-skeleton-line-short"></div>
      <div className="ACD-skeleton-line"></div>
      <div className="ACD-skeleton-line ACD-skeleton-line-medium"></div>
    </div>
  </div>
);

const StatPill = ({ icon, label, value }) => (
  <div className="ACD-stat-pill">
    <span className="ACD-stat-icon">
      <i className={icon} />
    </span>
    <span className="ACD-stat-value">{value}</span>
    <small className="ACD-stat-label">{label}</small>
  </div>
);

const AdminCourseDetailInner = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const { user } = useAuth();

  const canViewCourseAdmin = useMemo(
    () => hasAnyPermission(user, [PERMISSION_IDS.VER_PANEL_CURSOS, PERMISSION_IDS.VER_CURSOS]),
    [user],
  );

  useEffect(() => {
    if (user && !canViewCourseAdmin) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para ver la gestión de este curso.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canViewCourseAdmin, navigate, notify]);

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loadingCourse, setLoadingCourse] = useState(true);
  const [loadingModules, setLoadingModules] = useState(true);
  const [pending, setPending] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedClasses, setExpandedClasses] = useState(new Set());

  const toggleModule = useCallback((moduleId) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  }, []);

  const toggleClass = useCallback((classId) => {
    setExpandedClasses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(classId)) {
        newSet.delete(classId);
      } else {
        newSet.add(classId);
      }
      return newSet;
    });
  }, []);

  const [editState, setEditState] = useState({
    title: '',
    editingTitle: false,
    shortDescription: '',
    editingShort: false,
    fullDescription: '',
    editingFull: false,
  });

  const [discountDraft, setDiscountDraft] = useState(0);
  const [activateLoading, setActivateLoading] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleDraft, setModuleDraft] = useState({ id: null, name: '', shortDescription: '' });
  const [moduleLoading, setModuleLoading] = useState(false);

  const [selectedModule, setSelectedModule] = useState(null);

  const [showClassModal, setShowClassModal] = useState(false);
  const [classDraft, setClassDraft] = useState({
    id: null,
    moduleId: null,
    title: '',
    shortDescription: '',
    extendedDescription: '',
    videoUrl: '',
    durationMinutes: 0,
    orderIndex: 1,
  });
  const [classLoading, setClassLoading] = useState(false);
  const [classMaterials, setClassMaterials] = useState([]);
  const [uploadingMaterials, setUploadingMaterials] = useState(false);

  // Estados para exámenes
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [expandedExams, setExpandedExams] = useState(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [showExamModal, setShowExamModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [examDraft, setExamDraft] = useState({ id: null, titulo: '', descripcion: '', notaMinima: 6.0 });
  const [questionDraft, setQuestionDraft] = useState({ id: null, examenId: null, pregunta: '', tipo: 'unica', orden: 1 });
  const [answerDraft, setAnswerDraft] = useState({ id: null, preguntaId: null, texto: '', esCorrecta: false, orden: 1 });
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [examLoading, setExamLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const resetModuleDraft = useCallback(
    () => setModuleDraft({ id: null, name: '', shortDescription: '' }),
    [],
  );

  const resetClassDraft = useCallback(
    () =>
      setClassDraft({
        id: null,
        moduleId: null,
        title: '',
        shortDescription: '',
        extendedDescription: '',
        videoUrl: '',
        durationMinutes: 0,
        orderIndex: 1,
      }),
    [],
  );

  const fetchCourse = useCallback(async () => {
    if (!token || !courseId) return;
    setLoadingCourse(true);
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`No se pudo cargar el curso (${response.status})`);
      }

      const data = await response.json();
      const courseData = data.course || data;
      setCourse(courseData);
      setDiscountDraft(Number(courseData.discountPercent ?? 0));
      setSelectedCategoryId(courseData.categoriaId || courseData.categoria?.id || '');
      setEditState((prev) => ({
        ...prev,
        title: courseData.titulo || '',
        shortDescription: courseData.descripcionBreve || '',
        fullDescription: courseData.descripcion || '',
      }));
    } catch (error) {
      notify({
        title: 'Error al cargar el curso',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingCourse(false);
    }
  }, [courseId, notify, token]);

  const fetchModuleClasses = useCallback(
    async (moduleId) => {
      if (!token || !moduleId) return [];
      try {
        const response = await fetch(buildApiUrl(`/api/courses/modules/${moduleId}/classes`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`No se pudieron cargar las clases del módulo ${moduleId}.`);
        }
        const data = await response.json();
        log(`[AdminCourseDetail] Datos recibidos para módulo ${moduleId}:`, data);
        const list = Array.isArray(data) ? data : data.classes || [];
        log(`[AdminCourseDetail] Lista de clases normalizada:`, list.length, 'clases');
        const normalized = list.map((classItem) => {
          const normalizedClass = {
          id: classItem.id,
            titulo: classItem.titulo || classItem.name || classItem.nombre || 'Clase sin título',
            nombre: classItem.nombre || classItem.name || classItem.titulo || 'Clase sin título',
            descripcion: classItem.descripcion || classItem.descripcionBreve || classItem.shortDescription || '',
            descripcionBreve: classItem.descripcionBreve || classItem.shortDescription || classItem.descripcion || '',
            descripcionCompleta: classItem.descripcionCompleta || classItem.extendedDescription || '',
            shortDescription: classItem.shortDescription || classItem.descripcionBreve || classItem.descripcion || '',
            extendedDescription: classItem.extendedDescription || classItem.descripcionCompleta || '',
            videoUrl: classItem.videoUrl || classItem.linkVideo || '',
            linkVideo: classItem.linkVideo || classItem.videoUrl || '',
            duracionMinutos: classItem.duracionMinutos ?? classItem.minutes ?? classItem.duration ?? 0,
            duracion: classItem.duracion ?? classItem.duracionMinutos ?? classItem.minutes ?? 0,
            orden: classItem.orden ?? classItem.orderIndex ?? 0,
          orderIndex: classItem.orderIndex ?? classItem.orden ?? 0,
          };
          log(`[AdminCourseDetail] Clase normalizada ${normalizedClass.id}:`, {
            nombre: normalizedClass.nombre,
            descripcionBreve: normalizedClass.descripcionBreve,
            descripcionCompleta: normalizedClass.descripcionCompleta,
            orden: normalizedClass.orden,
            videoUrl: normalizedClass.videoUrl,
          });
          return normalizedClass;
        });
        return normalized;
      } catch (error) {
        notify({
          title: 'Error al cargar clases',
          message: error.message,
          variant: 'error',
        });
        return [];
      }
    },
    [notify, token],
  );

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    setLoadingCategories(true);
    try {
      const response = await fetch(buildApiUrl('/api/courses/categories'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar categorías (${response.status})`);
      }
      const data = await response.json();
      setCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (error) {
      logError('[AdminCourseDetail] Error fetching categories:', error);
      notify({
        title: 'Error al cargar categorías',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingCategories(false);
    }
  }, [token, notify]);

  const fetchModules = useCallback(async () => {
    if (!token || !courseId) return;
    setLoadingModules(true);
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}/modules`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('No pudimos obtener los módulos del curso.');
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : data.modules || [];
      const normalised = await Promise.all(
        list.map(async (module) => {
          const clases = ensureArray(module.clases || module.classes);
          // Si las clases ya tienen datos completos (nombre, descripcionBreve, etc.), usarlas directamente
          // Si solo tienen id, obtenerlas completas
          const hasCompleteData = clases.length > 0 && clases[0]?.nombre !== undefined;
          const resolvedClasses = hasCompleteData 
            ? clases.map(clase => ({
                id: clase.id,
                titulo: clase.titulo || clase.name || clase.nombre || 'Clase sin título',
                nombre: clase.nombre || clase.name || clase.titulo || 'Clase sin título',
                descripcion: clase.descripcion || clase.descripcionBreve || clase.shortDescription || '',
                descripcionBreve: clase.descripcionBreve || clase.shortDescription || clase.descripcion || '',
                descripcionCompleta: clase.descripcionCompleta || clase.extendedDescription || '',
                shortDescription: clase.shortDescription || clase.descripcionBreve || clase.descripcion || '',
                extendedDescription: clase.extendedDescription || clase.descripcionCompleta || '',
                videoUrl: clase.videoUrl || clase.linkVideo || '',
                linkVideo: clase.linkVideo || clase.videoUrl || '',
                minutes: clase.minutes ?? clase.duracionMinutos ?? clase.duration ?? 0,
                duracionMinutos: clase.duracionMinutos ?? clase.minutes ?? clase.duration ?? 0,
                duracion: clase.duracion ?? clase.duracionMinutos ?? clase.minutes ?? 0,
                orden: clase.orden ?? clase.orderIndex ?? 0,
                orderIndex: clase.orderIndex ?? clase.orden ?? 0,
              }))
            : await fetchModuleClasses(module.id);
          log(`[AdminCourseDetail] Módulo ${module.id} tiene ${resolvedClasses.length} clases:`, resolvedClasses);
          // Calcular totalMinutes sumando los minutos de todas las clases
          const calculatedTotalMinutes = resolvedClasses.reduce((sum, clase) => {
            const minutos = clase.minutes ?? clase.duracionMinutos ?? clase.duracion ?? 0;
            return sum + Number(minutos);
          }, 0);
          return {
            id: module.id,
            name: module.name || module.titulo || module.nombre || 'Módulo sin título',
            titulo: module.titulo || module.nombre || module.name || 'Módulo sin título',
            shortDescription: module.shortDescription || module.descripcion || module.descripcionBreve || '',
            descripcion: module.descripcion || module.descripcionBreve || module.shortDescription || '',
            descripcionBreve: module.descripcionBreve || module.descripcion || module.shortDescription || '',
            orderIndex: module.orderIndex || module.orden || 0,
            totalClasses: module.totalClasses ?? module.classCount ?? resolvedClasses.length ?? 0,
            totalMinutes: calculatedTotalMinutes || (module.totalMinutes ?? module.minutes ?? 0),
            clases: resolvedClasses,
          };
        }),
      );
      setModules(normalised);
    } catch (error) {
      notify({
        title: 'Error al cargar los módulos',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingModules(false);
    }
  }, [courseId, notify, token]);

  // Verificar permiso para editar exámenes
  const hasEditExamsPermission = useMemo(() => {
    if (!user) return false;
    const isAdmin = user.role === 'Admin' || user.rol === 'Admin' || user.role === 'admin' || user.rol === 'admin';
    if (isAdmin) return true;
    const permissionIds = user.permissionIds || [];
    return permissionIds.includes(PERMISSION_IDS.EDITAR_EXAMENES_CURSOS);
  }, [user]);

  // Función para obtener un examen completo con preguntas y respuestas
  const fetchExamDetails = useCallback(async (examId) => {
    if (!token || !examId) return null;
    try {
      const response = await fetch(buildApiUrl(`/api/exams/${examId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('No se pudo cargar el examen.');
      }
      const data = await response.json();
      return data.exam || null;
    } catch (error) {
      notify({
        title: 'Error al cargar examen',
        message: error.message,
        variant: 'error',
      });
      return null;
    }
  }, [token, notify]);

  // Función para obtener exámenes del curso
  const fetchExams = useCallback(async () => {
    if (!token || !courseId || !hasEditExamsPermission) return;
    setLoadingExams(true);
    try {
      const response = await fetch(buildApiUrl(`/api/exams/courses/${courseId}/exams`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('No se pudieron cargar los exámenes del curso.');
      }
      const data = await response.json();
      const examsList = Array.isArray(data.exams) ? data.exams : [];
      
      // Obtener detalles completos de cada examen (con preguntas y respuestas)
      const examsWithDetails = await Promise.all(
        examsList.map(async (exam) => {
          const examDetails = await fetchExamDetails(exam.id);
          return examDetails || exam;
        })
      );
      
      setExams(examsWithDetails);
    } catch (error) {
      notify({
        title: 'Error al cargar exámenes',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingExams(false);
    }
  }, [courseId, token, notify, hasEditExamsPermission, fetchExamDetails]);

  useEffect(() => {
    fetchCourse();
    fetchModules();
    fetchCategories();
    if (hasEditExamsPermission) {
      fetchExams();
    }
  }, [fetchCourse, fetchModules, fetchCategories, fetchExams, hasEditExamsPermission]);

  const refreshAll = async () => {
    setPending(true);
    await Promise.all([fetchCourse(), fetchModules(), fetchCategories()]);
    setPending(false);
    notify({
      title: 'Datos actualizados',
      message: 'Se sincronizaron los datos del curso exitosamente.',
      variant: 'success',
    });
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategoryId(categoryId);
    const success = await handleUpdateCourse(
      { categoriaId: categoryId ? parseInt(categoryId) : null },
      'La categoría se actualizó correctamente.'
    );
    if (!success) {
      // Revertir el cambio si falló
      setSelectedCategoryId(course?.categoriaId || course?.categoria?.id || '');
    }
  };

  const handleUpdateCourse = async (payload, successMessage) => {
    if (!token || !courseId) return false;
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...(course || {}),
          ...payload,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'No se pudo actualizar el curso.');
      }
      const updated = await response.json();
      const courseData = updated.course || updated;
      
      // Normalizar los datos del curso para asegurar compatibilidad
      // Preservar las estadísticas existentes si no vienen en la respuesta
      const normalizedCourse = {
        ...course,
        ...courseData,
        titulo: courseData.titulo || courseData.nombre || course?.titulo || '',
        descripcionBreve: courseData.descripcionBreve !== undefined ? courseData.descripcionBreve : (course?.descripcionBreve || ''),
        descripcion: courseData.descripcion !== undefined ? courseData.descripcion : (courseData.descripcionExtendida || course?.descripcion || ''),
        descripcionExtendida: courseData.descripcionExtendida !== undefined ? courseData.descripcionExtendida : (course?.descripcionExtendida || ''),
        // Preservar estadísticas si no vienen en la respuesta
        moduleCount: courseData.moduleCount !== undefined ? courseData.moduleCount : (course?.moduleCount || 0),
        classCount: courseData.classCount !== undefined ? courseData.classCount : (course?.classCount || 0),
        totalMinutes: courseData.totalMinutes !== undefined ? courseData.totalMinutes : (course?.totalMinutes || 0),
      };
      
      setCourse(normalizedCourse);
      setSelectedCategoryId(normalizedCourse.categoriaId || normalizedCourse.categoria?.id || '');
      setEditState((prev) => ({
        ...prev,
        title: normalizedCourse.titulo || normalizedCourse.nombre || '',
        shortDescription: normalizedCourse.descripcionBreve || '',
        fullDescription: normalizedCourse.descripcion || normalizedCourse.descripcionExtendida || '',
      }));
      if (successMessage) {
        notify({
          title: 'Curso actualizado',
          message: successMessage,
          variant: 'success',
        });
      }
      return true;
    } catch (error) {
      notify({
        title: 'Error al actualizar',
        message: error.message,
        variant: 'error',
      });
      return false;
    }
  };

  const handleSaveTitle = async () => {
    const title = editState.title.trim();
    if (!title) {
      notify({
        title: 'Título requerido',
        message: 'Ingresa un título válido antes de guardar.',
        variant: 'warning',
      });
      return;
    }
    const success = await handleUpdateCourse({ titulo: title }, 'El título se actualizó correctamente.');
    if (success) {
      setEditState((prev) => ({ ...prev, editingTitle: false }));
    }
  };

  const handleSaveShortDescription = async () => {
    const description = editState.shortDescription.trim();
    const success = await handleUpdateCourse(
      { descripcionBreve: description },
      'La descripción breve fue actualizada.',
    );
    if (success) {
      // Actualizar el estado del curso inmediatamente con el nuevo valor
      setCourse((prev) => ({
        ...prev,
        descripcionBreve: description,
      }));
      setEditState((prev) => ({ ...prev, editingShort: false }));
    }
  };

  const handleSaveFullDescription = async () => {
    const description = editState.fullDescription.trim();
    const success = await handleUpdateCourse(
      { descripcion: description },
      'La descripción completa fue actualizada.',
    );
    if (success) {
      // Actualizar el estado del curso inmediatamente con el nuevo valor
      setCourse((prev) => ({
        ...prev,
        descripcion: description,
        descripcionExtendida: description, // También actualizar el alias
      }));
      setEditState((prev) => ({ ...prev, editingFull: false }));
    }
  };

  const handleDiscountSave = async () => {
    const value = Number(discountDraft);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      notify({
        title: 'Valor inválido',
        message: 'El descuento debe estar entre 0% y 100%.',
        variant: 'warning',
      });
      return;
    }
    await handleUpdateCourse({ discountPercent: value }, 'El descuento fue actualizado.');
  };

  const handleToggleArchive = async () => {
    if (!course) return;
    const action = course.archivado ? 'desarchivar' : 'archivar';
    const confirmed = window.confirm(
      `¿Seguro que quieres ${action} el curso "${course.titulo}"? Podrás revertir esta acción más adelante.`,
    );
    if (!confirmed) return;
    await handleUpdateCourse({ archivado: !course.archivado }, 'El estado de archivado se actualizó.');
  };

  const handleActivateCourse = async () => {
    if (modules.length === 0) {
      notify({
        title: 'No hay módulos',
        message: 'Debes crear al menos un módulo antes de activar el curso.',
        variant: 'warning',
      });
      return;
    }
    const hasClasses = modules.some((module) => (module.clases?.length || 0) > 0);
    if (!hasClasses) {
      notify({
        title: 'Faltan clases',
        message: 'Agrega al menos una clase a tus módulos antes de activar el curso.',
        variant: 'warning',
      });
      return;
    }
    setActivateLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...(course || {}),
          estado: 'activo',
        }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No pudimos activar el curso.');
      }
      notify({
        title: 'Curso activado',
        message: 'El curso quedó activado. Puedes configurar el precio en la sección de Productos.',
        variant: 'success',
      });
      await fetchCourse();
    } catch (error) {
      notify({
        title: 'Error al activar el curso',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setActivateLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    event.preventDefault();
    if (!imageFile) return;
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append('courseImage', imageFile);
      if (course) {
        formData.append('titulo', course.titulo || '');
        formData.append('descripcionBreve', course.descripcionBreve || '');
        formData.append('descripcion', course.descripcion || '');
        formData.append('estado', course.estado || 'inactivo');
        formData.append('archivado', course.archivado ? 1 : 0);
      }

      const response = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No pudimos actualizar la imagen.');
      }

      notify({
        title: 'Imagen actualizada',
        message: 'La imagen del curso se guardó correctamente.',
        variant: 'success',
      });
      setShowImageModal(false);
      setImageFile(null);
      await fetchCourse();
    } catch (error) {
      notify({
        title: 'Error al subir la imagen',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setImageLoading(false);
    }
  };

  const openModuleModal = (module = null) => {
    setSelectedModule(module);
    setModuleDraft({
      id: module?.id ?? null,
      name: module?.name || module?.titulo || module?.nombre || '',
      shortDescription: module?.shortDescription || module?.descripcion || '',
    });
    setShowModuleModal(true);
  };

  const handleSaveModule = async () => {
    if (!moduleDraft.name.trim()) {
      notify({
        title: 'Título requerido',
        message: 'El módulo debe tener un título para guardarse.',
        variant: 'warning',
      });
      return;
    }
    setModuleLoading(true);
    const payload = {
      name: moduleDraft.name.trim(),
      shortDescription: moduleDraft.shortDescription.trim(),
    };
    try {
      const url = moduleDraft.id
        ? `/api/courses/modules/${moduleDraft.id}`
        : `/api/courses/${courseId}/modules`;
      const method = moduleDraft.id ? 'PUT' : 'POST';
      const response = await fetch(buildApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No pudimos guardar el módulo.');
      }
      const result = await response.json();
      // Si el backend devuelve el módulo actualizado, actualizar el estado local
      if (result.module) {
        setModules((prev) =>
          prev.map((m) =>
            m.id === result.module.id
              ? {
                  ...m,
                  nombre: result.module.nombre,
                  titulo: result.module.titulo || result.module.nombre,
                  descripcionBreve: result.module.descripcionBreve || '',
                  descripcion: result.module.descripcion || result.module.descripcionBreve || '',
                  shortDescription: result.module.shortDescription || result.module.descripcionBreve || '',
                }
              : m
          )
        );
      }
      notify({
        title: 'Módulo guardado',
        message: moduleDraft.id
          ? 'El módulo se actualizó correctamente.'
          : 'El módulo se creó correctamente.',
        variant: 'success',
      });
      handleCloseModuleModal();
      resetModuleDraft();
      await fetchModules();
      await fetchCourse();
    } catch (error) {
      notify({
        title: 'Error al guardar el módulo',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setModuleLoading(false);
    }
  };

  const openClassModal = (module, classItem = null) => {
    setSelectedModule(module);
    setClassDraft({
      id: classItem?.id ?? null,
      moduleId: module.id,
      title: classItem?.titulo || classItem?.nombre || '',
      shortDescription: classItem?.shortDescription || classItem?.descripcionBreve || classItem?.descripcion || '',
      extendedDescription: classItem?.extendedDescription || classItem?.descripcionCompleta || '',
      videoUrl: classItem?.videoUrl || classItem?.video || '',
      durationMinutes: classItem?.duracionMinutos ?? classItem?.duracion ?? 0,
      orderIndex: classItem?.orderIndex ?? classItem?.orden ?? 1,
    });
    setShowClassModal(true);
  };

  const handleCloseModuleModal = () => {
    setShowModuleModal(false);
    setSelectedModule(null);
    resetModuleDraft();
  };

  const handleCloseClassModal = () => {
    setShowClassModal(false);
    setSelectedModule(null);
    resetClassDraft();
    setClassMaterials([]);
  };

  // Funciones para exámenes
  const toggleExam = useCallback((examId) => {
    setExpandedExams((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(examId)) {
        newSet.delete(examId);
      } else {
        newSet.add(examId);
      }
      return newSet;
    });
  }, []);

  const toggleQuestion = useCallback((questionId) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  }, []);

  const resetExamDraft = useCallback(() => {
    setExamDraft({ id: null, titulo: '', descripcion: '', notaMinima: 6.0 });
  }, []);

  const resetQuestionDraft = useCallback(() => {
    setQuestionDraft({ id: null, examenId: null, pregunta: '', tipo: 'unica', orden: 1 });
  }, []);

  const resetAnswerDraft = useCallback(() => {
    setAnswerDraft({ id: null, preguntaId: null, texto: '', esCorrecta: false, orden: 1 });
  }, []);

  const openExamModal = (exam = null) => {
    if (exam) {
      setExamDraft({
        id: exam.id,
        titulo: exam.titulo || '',
        descripcion: exam.descripcion || '',
        notaMinima: exam.notaMinima || 6.0,
      });
    } else {
      resetExamDraft();
    }
    setShowExamModal(true);
  };

  const openQuestionModal = (exam, question = null) => {
    setSelectedExam(exam);
    if (question) {
      setQuestionDraft({
        id: question.id,
        examenId: exam.id,
        pregunta: question.pregunta || '',
        tipo: question.tipo || 'unica',
        orden: question.orden || 1,
      });
    } else {
      setQuestionDraft({
        id: null,
        examenId: exam.id,
        pregunta: '',
        tipo: 'unica',
        orden: (exam.preguntas?.length || 0) + 1,
      });
    }
    setShowQuestionModal(true);
  };

  const openAnswerModal = (question, answer = null) => {
    setSelectedQuestion(question);
    if (answer) {
      setAnswerDraft({
        id: answer.id,
        preguntaId: question.id,
        texto: answer.texto || '',
        esCorrecta: answer.esCorrecta || false,
        orden: answer.orden || 1,
      });
    } else {
      setAnswerDraft({
        id: null,
        preguntaId: question.id,
        texto: '',
        esCorrecta: false,
        orden: (question.respuestas?.length || 0) + 1,
      });
    }
    setShowAnswerModal(true);
  };

  const handleSaveExam = async () => {
    if (!examDraft.titulo.trim()) {
      notify({
        title: 'Título requerido',
        message: 'El examen debe tener un título.',
        variant: 'warning',
      });
      return;
    }
    setExamLoading(true);
    try {
      const url = examDraft.id ? `/api/exams/${examDraft.id}` : '/api/exams';
      const method = examDraft.id ? 'PUT' : 'POST';
      const payload = {
        cursoId: parseInt(courseId),
        titulo: examDraft.titulo.trim(),
        descripcion: examDraft.descripcion.trim() || null,
        notaMinima: parseFloat(examDraft.notaMinima) || 6.0,
      };
      const response = await fetch(buildApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No se pudo guardar el examen.');
      }
      notify({
        title: 'Examen guardado',
        message: examDraft.id ? 'El examen se actualizó correctamente.' : 'El examen se creó correctamente.',
        variant: 'success',
      });
      setShowExamModal(false);
      resetExamDraft();
      await fetchExams();
    } catch (error) {
      notify({
        title: 'Error al guardar examen',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setExamLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!questionDraft.pregunta.trim()) {
      notify({
        title: 'Pregunta requerida',
        message: 'La pregunta no puede estar vacía.',
        variant: 'warning',
      });
      return;
    }
    setQuestionLoading(true);
    try {
      const url = questionDraft.id
        ? `/api/exams/questions/${questionDraft.id}`
        : `/api/exams/${questionDraft.examenId}/questions`;
      const method = questionDraft.id ? 'PUT' : 'POST';
      const payload = {
        pregunta: questionDraft.pregunta.trim(),
        tipo: questionDraft.tipo,
        // El orden se calcula automáticamente en el backend
      };
      const response = await fetch(buildApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No se pudo guardar la pregunta.');
      }
      notify({
        title: 'Pregunta guardada',
        message: questionDraft.id ? 'La pregunta se actualizó correctamente.' : 'La pregunta se creó correctamente.',
        variant: 'success',
      });
      setShowQuestionModal(false);
      resetQuestionDraft();
      setSelectedExam(null);
      await fetchExams();
    } catch (error) {
      notify({
        title: 'Error al guardar pregunta',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setQuestionLoading(false);
    }
  };

  const handleSaveAnswer = async () => {
    if (!answerDraft.texto.trim()) {
      notify({
        title: 'Texto requerido',
        message: 'La respuesta no puede estar vacía.',
        variant: 'warning',
      });
      return;
    }
    setAnswerLoading(true);
    try {
      const url = answerDraft.id
        ? `/api/exams/answers/${answerDraft.id}`
        : `/api/exams/questions/${answerDraft.preguntaId}/answers`;
      const method = answerDraft.id ? 'PUT' : 'POST';
      const payload = {
        texto: answerDraft.texto.trim(),
        esCorrecta: answerDraft.esCorrecta,
        // El orden se calcula automáticamente en el backend
      };
      const response = await fetch(buildApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No se pudo guardar la respuesta.');
      }
      notify({
        title: 'Respuesta guardada',
        message: answerDraft.id ? 'La respuesta se actualizó correctamente.' : 'La respuesta se creó correctamente.',
        variant: 'success',
      });
      setShowAnswerModal(false);
      resetAnswerDraft();
      setSelectedQuestion(null);
      await fetchExams();
    } catch (error) {
      notify({
        title: 'Error al guardar respuesta',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setAnswerLoading(false);
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este examen? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      const response = await fetch(buildApiUrl(`/api/exams/${examId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('No se pudo eliminar el examen.');
      }
      notify({
        title: 'Examen eliminado',
        message: 'El examen se eliminó correctamente.',
        variant: 'success',
      });
      await fetchExams();
    } catch (error) {
      notify({
        title: 'Error al eliminar examen',
        message: error.message,
        variant: 'error',
      });
    }
  };

  const handleSaveClass = async () => {
    if (!classDraft.title.trim()) {
      notify({
        title: 'Título requerido',
        message: 'La clase necesita un título.',
        variant: 'warning',
      });
      return;
    }
    if (!classDraft.moduleId) {
      notify({
        title: 'Módulo no seleccionado',
        message: 'Selecciona un módulo válido.',
        variant: 'warning',
      });
      return;
    }
    setClassLoading(true);
    const payload = {
      name: classDraft.title.trim(),
      shortDescription: classDraft.shortDescription.trim(),
      extendedDescription: classDraft.extendedDescription.trim(),
      videoUrl: classDraft.videoUrl.trim(),
      minutes: Number(classDraft.durationMinutes) || 0,
      orderIndex: Number(classDraft.orderIndex) || 1,
    };
    try {
      let classId = classDraft.id;
      if (classDraft.id) {
        const response = await fetch(buildApiUrl(`/api/courses/classes/${classDraft.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'No pudimos actualizar la clase.');
        }
        const updateResult = await response.json();
        // Si el backend devuelve la clase actualizada, actualizar el estado local
        if (updateResult.class) {
          setModules((prev) =>
            prev.map((module) => {
              if (module.id === classDraft.moduleId) {
                return {
                  ...module,
                  clases: module.clases.map((c) =>
                    c.id === updateResult.class.id
                      ? {
                          ...c,
                          nombre: updateResult.class.nombre,
                          titulo: updateResult.class.titulo || updateResult.class.nombre,
                          descripcionBreve: updateResult.class.descripcionBreve || '',
                          descripcionCompleta: updateResult.class.descripcionCompleta || '',
                          shortDescription: updateResult.class.shortDescription || updateResult.class.descripcionBreve || '',
                          extendedDescription: updateResult.class.extendedDescription || updateResult.class.descripcionCompleta || '',
                          videoUrl: updateResult.class.videoUrl || '',
                          linkVideo: updateResult.class.linkVideo || '',
                          minutes: updateResult.class.minutes || 0,
                          duracionMinutos: updateResult.class.duracionMinutos || updateResult.class.minutes || 0,
                          orden: updateResult.class.orden || 0,
                          orderIndex: updateResult.class.orderIndex || updateResult.class.orden || 0,
                        }
                      : c
                  ),
                };
              }
              return module;
            })
          );
        }
      } else {
        const response = await fetch(
          buildApiUrl(`/api/courses/modules/${classDraft.moduleId}/classes`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          },
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'No pudimos crear la clase.');
        }
        const data = await response.json();
        classId = data.classId || data.id;
      }

      // Subir materiales si hay archivos y es una clase nueva o se están agregando materiales
      if (classMaterials.length > 0 && classId) {
        await handleUploadMaterials(classId);
      }

      notify({
        title: 'Clase guardada',
        message: classDraft.id ? 'La clase se actualizó.' : 'La clase fue creada.',
        variant: 'success',
      });
      handleCloseClassModal();
      resetClassDraft();
      setClassMaterials([]);
      await fetchModules();
      await fetchCourse();
    } catch (error) {
      notify({
        title: 'Error al guardar la clase',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setClassLoading(false);
    }
  };

  const handleUploadMaterials = async (classId) => {
    if (!classMaterials.length || !classId) return;
    setUploadingMaterials(true);
    try {
      const formData = new FormData();
      classMaterials.forEach((file) => {
        formData.append('materials', file);
      });

      const response = await fetch(buildApiUrl(`/api/courses/classes/${classId}/materials`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No pudimos subir los materiales.');
      }

      notify({
        title: 'Materiales subidos',
        message: `Se subieron ${classMaterials.length} material(es) correctamente.`,
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error al subir materiales',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setUploadingMaterials(false);
    }
  };

  const statusBadge = useMemo(() => {
    if (!course) return null;
    if (course.archivado) {
      return <Badge bg="dark">Archivado</Badge>;
    }
    if (course.estado === 'activo') {
      return <Badge bg="success">Activo</Badge>;
    }
    if (course.estado === 'pendiente') {
      return <Badge bg="warning" text="dark">Pendiente</Badge>;
    }
    return <Badge bg="secondary">Inactivo</Badge>;
  }, [course]);

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="courses"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="ACD-container">
          <div className="ACD-header">
            <div className="ACD-header-content">
              <Button variant="link" className="ACD-back-button" onClick={() => navigate(-1)}>
                <i className="bi-arrow-left-circle me-2" />
                Volver
              </Button>
              <h1 className="ACD-title">
                <i className="bi-journal-text" />
                {loadingCourse ? 'Cargando curso...' : course?.titulo || 'Curso sin título'}
              </h1>
              <p className="ACD-subtitle">
                Actualiza contenidos, módulos y clases del curso seleccionado.
              </p>
            </div>
            <div className="ACD-actions">
              <Button variant="outline-secondary" onClick={refreshAll} disabled={pending}>
                {pending ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Actualizando
                  </>
                ) : (
                  <>
                    <i className="bi-arrow-repeat me-2" />
                    Sincronizar datos
                  </>
                )}
              </Button>
              <Button variant={course?.archivado ? 'success' : 'outline-secondary'} onClick={handleToggleArchive}>
                <i className={`bi-${course?.archivado ? 'arrow-counterclockwise' : 'archive'} me-2`} />
                {course?.archivado ? 'Desarchivar' : 'Archivar'}
              </Button>
              {!course?.estado || course.estado !== 'activo' ? (
                <Button variant="primary" onClick={handleActivateCourse} disabled={activateLoading}>
                  {activateLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Activando...
                    </>
                  ) : (
                    <>
                      <i className="bi-play-circle me-2" />
                      Activar curso
                    </>
                  )}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="ACD-course-card">
            <div className="ACD-course-image-wrapper">
              {loadingCourse ? (
                <div className="ACD-image-placeholder">
                  <Placeholder animation="glow" style={{ height: '100%', minHeight: 300, display: 'block' }}>
                    <Placeholder style={{ height: 300 }} />
                  </Placeholder>
                </div>
              ) : (
                <div className="ACD-image-section">
                  <img
                    src={sanitiseImage(course?.imagen) || FALLBACK_IMAGE}
                    alt={course?.titulo || 'Imagen del curso'}
                    className="ACD-course-image"
                  />
                  <div className="ACD-image-overlay">
                    <Button
                      variant="light"
                      className="ACD-image-button"
                      onClick={() => setShowImageModal(true)}
                    >
                      <i className="bi-camera-video me-2" />
                      Cambiar imagen
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="ACD-course-content">
                  <div className="ACD-badges-container">
                    {statusBadge}
                    {course?.discountPercent > 0 && (
                      <Badge bg="info">-{course.discountPercent}% descuento</Badge>
                    )}
                  </div>
                  <div className="ACD-title-section">
                    <div className="ACD-title-edit">
                      {editState.editingTitle ? (
                        <>
                          <Form.Control
                            value={editState.title}
                            onChange={(event) =>
                              setEditState((prev) => ({ ...prev, title: event.target.value }))
                            }
                            maxLength={120}
                            className="ACD-title-input"
                          />
                          <div className="ACD-title-actions">
                            <Button variant="success" onClick={handleSaveTitle}>
                              Guardar
                            </Button>
                            <Button
                              variant="outline-secondary"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  editingTitle: false,
                                  title: course?.titulo || '',
                                }))
                              }
                            >
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <h2 className="ACD-course-title-text">{course?.titulo || 'Curso sin título'}</h2>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setEditState((prev) => ({ ...prev, editingTitle: true }))}
                          >
                            <i className="bi-pencil" />
                          </Button>
                        </>
                      )}
                    </div>
                    <p className="ACD-course-id">
                      ID del curso: <code>{course?.id}</code>
                    </p>
                  </div>

                  <div className="ACD-category-section">
                    <Form.Group className="ACD-category-group">
                      <Form.Label className="ACD-category-label">
                        <i className="bi-folder-fill me-2" />
                        Categoría
                      </Form.Label>
                      <Form.Select
                        value={selectedCategoryId}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        disabled={loadingCategories}
                        className="ACD-category-select"
                      >
                        <option value="">Sin categoría</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.nombre}
                          </option>
                        ))}
                      </Form.Select>
                      {loadingCategories && (
                        <small className="text-muted d-block mt-2">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Cargando categorías...
                        </small>
                      )}
                    </Form.Group>
                  </div>

                  <div className="ACD-stats-container">
                    <div className="ACD-stats-grid">
                      <StatPill
                        icon="bi-diagram-3"
                        label="Módulos"
                        value={loadingCourse ? '--' : course?.moduleCount ?? 0}
                      />
                      <StatPill
                        icon="bi-play-circle"
                        label="Clases"
                        value={loadingCourse ? '--' : course?.classCount ?? 0}
                      />
                      <StatPill
                        icon="bi-clock-history"
                        label="Duración total"
                        value={loadingCourse ? '--' : minutesToDisplay(course?.totalMinutes)}
                      />
                    </div>
                  </div>

                  <div className="ACD-description-section">
                    <div>
                      <div className="ACD-description-header">
                        <h3 className="ACD-description-title">Descripción breve</h3>
                        {editState.editingShort ? (
                          <>
                            <Button variant="success" size="sm" onClick={handleSaveShortDescription}>
                              Guardar
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  editingShort: false,
                                  shortDescription: course?.descripcionBreve || '',
                                }))
                              }
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setEditState((prev) => ({ ...prev, editingShort: true }))}
                          >
                            <i className="bi-pencil" />
                          </Button>
                        )}
                      </div>
                      {editState.editingShort ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          maxLength={320}
                          value={editState.shortDescription}
                          onChange={(event) =>
                            setEditState((prev) => ({ ...prev, shortDescription: event.target.value }))
                          }
                        />
                      ) : (
                        <p className="ACD-description-content">
                          {course?.descripcionBreve || 'Añade una descripción breve para destacar el curso.'}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="ACD-description-header">
                        <h3 className="ACD-description-title">Descripción completa</h3>
                        {editState.editingFull ? (
                          <>
                            <Button variant="success" size="sm" onClick={handleSaveFullDescription}>
                              Guardar
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                setEditState((prev) => ({
                                  ...prev,
                                  editingFull: false,
                                  fullDescription: course?.descripcion || '',
                                }))
                              }
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setEditState((prev) => ({ ...prev, editingFull: true }))}
                          >
                            <i className="bi-pencil" />
                          </Button>
                        )}
                      </div>
                      {editState.editingFull ? (
                        <Form.Control
                          as="textarea"
                          rows={6}
                          maxLength={4000}
                          value={editState.fullDescription}
                          onChange={(event) =>
                            setEditState((prev) => ({ ...prev, fullDescription: event.target.value }))
                          }
                        />
                      ) : (
                        <p className="ACD-description-content">
                          {course?.descripcion || 'Agrega una descripción completa para informar a tus estudiantes.'}
                        </p>
                      )}
                    </div>

                    <div className="ACD-discount-section">
                      <div className="ACD-discount-content">
                        <div className="ACD-discount-label">Descuento</div>
                        <p className="ACD-discount-help">
                          Define un porcentaje de descuento global para este curso.
                        </p>
                        <div className="ACD-discount-controls">
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            value={discountDraft}
                            onChange={(event) => setDiscountDraft(event.target.value)}
                            className="ACD-discount-input"
                          />
                          <span className="ACD-discount-percent">%</span>
                          <Button variant="outline-primary" onClick={handleDiscountSave}>
                            Guardar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

          <section className="ACD-modules-section">
            <div className="ACD-modules-container">
            <div className="ACD-modules-header">
              <div>
                <h2 className="ACD-modules-title">Módulos del curso</h2>
                <p className="ACD-modules-subtitle">
                  Organiza los contenidos en módulos y clases para ofrecer una experiencia estructurada.
                </p>
              </div>
                <button className="ACD-btn ACD-btn-primary" onClick={() => openModuleModal()}>
                  <i className="bi-plus-circle" />
                Crear módulo
                </button>
            </div>

            {loadingModules ? (
                <div className="ACD-modules-content">
                <ModuleSkeleton />
                <ModuleSkeleton />
                </div>
            ) : modules.length === 0 ? (
                <div className="ACD-modules-content">
                  <div className="ACD-empty-modules">
                  <i className="bi-diagram-3 ACD-empty-icon" />
                  <h5 className="ACD-empty-title">Aún no hay módulos creados</h5>
                  <p className="ACD-empty-description">
                    Crea tu primer módulo para comenzar a agregar clases y estructurar el curso.
                  </p>
                    <button className="ACD-btn ACD-btn-primary" onClick={() => openModuleModal()}>
                    Crear módulo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ACD-modules-content">
                  <div className="ACD-modules-list">
                {modules
                  .slice()
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((module) => (
                    <div key={module.id} className="ACD-module-item">
                      <div 
                        className="ACD-module-header ACD-module-header-clickable"
                        onClick={() => toggleModule(module.id)}
                      >
                        <div className="ACD-module-header-left">
                          <i className={`bi-chevron-${expandedModules.has(module.id) ? 'down' : 'right'} ACD-module-chevron`} />
                          <span className="ACD-module-order-badge">
                            Módulo #{module.orderIndex ?? module.orden ?? module.id}
                          </span>
                          <h3 className="ACD-module-title">{module.titulo || module.nombre || module.name || 'Módulo sin título'}</h3>
                        </div>
                        <div className="ACD-module-header-right" onClick={(e) => e.stopPropagation()}>
                          <span className="ACD-module-meta">
                            {module.totalClasses} clases · {minutesToDisplay(module.totalMinutes)}
                          </span>
                          <div className="ACD-module-actions-header">
                            <button className="ACD-btn ACD-btn-outline-primary ACD-btn-sm" onClick={() => openModuleModal(module)}>
                              <i className="bi-pencil" />
                            Editar módulo
                            </button>
                            <button className="ACD-btn ACD-btn-outline-success ACD-btn-sm" onClick={() => openClassModal(module)}>
                              <i className="bi-plus-circle" />
                            Nueva clase
                            </button>
                        </div>
                        </div>
                      </div>
                      {expandedModules.has(module.id) && (
                      <div className="ACD-module-content">
                        <p className="ACD-module-description">{module.descripcionBreve || module.descripcion || module.shortDescription || 'Sin descripción disponible.'}</p>
                        <div className="ACD-classes-container">
                          {module.clases && module.clases.length > 0 ? (
                            <div className="ACD-classes-list">
                              {module.clases
                              .slice()
                              .sort((a, b) => (a.orderIndex ?? a.orden ?? 0) - (b.orderIndex ?? b.orden ?? 0))
                              .map((classItem) => (
                                  <div key={classItem.id} className="ACD-class-card-vertical">
                                    <div 
                                      className="ACD-class-card-header ACD-class-header-clickable"
                                      onClick={() => toggleClass(classItem.id)}
                                    >
                                      <div className="ACD-class-header-content">
                                        <div className="ACD-class-header-left">
                                          <i className={`bi-chevron-${expandedClasses.has(classItem.id) ? 'down' : 'right'} ACD-class-chevron`} />
                                          <span className="ACD-class-order-badge">
                                            #{classItem.orderIndex ?? classItem.orden ?? '-'}
                                          </span>
                                          <h5 className="ACD-class-title">
                                            {classItem.titulo || classItem.nombre || classItem.name || classItem.title || 'Clase sin título'}
                                          </h5>
                                        </div>
                                        <button
                                          className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openClassModal(module, classItem);
                                          }}
                                        >
                                          <i className="bi-pencil" />
                                          Editar
                                        </button>
                                      </div>
                                    </div>
                                    {expandedClasses.has(classItem.id) && (
                                    <div className="ACD-class-card-body">
                                      <div className="ACD-class-info-vertical">
                                        <div className="ACD-class-info-item">
                                          <i className="bi-file-text ACD-class-info-icon" />
                                          <div className="ACD-class-info-content">
                                            <span className="ACD-class-info-label">Descripción breve</span>
                                            <span className="ACD-class-info-value">
                                              {classItem.descripcionBreve || classItem.shortDescription || 'Sin descripción breve'}
                                            </span>
                                          </div>
                                        </div>
                                        {classItem.descripcionCompleta || classItem.extendedDescription ? (
                                          <div className="ACD-class-info-item">
                                            <i className="bi-file-text-fill ACD-class-info-icon" />
                                            <div className="ACD-class-info-content">
                                              <span className="ACD-class-info-label">Descripción completa</span>
                                              <span className="ACD-class-info-value">
                                                {classItem.descripcionCompleta || classItem.extendedDescription}
                                              </span>
                                            </div>
                                          </div>
                                        ) : null}
                                        {classItem.videoUrl || classItem.linkVideo ? (
                                          <div className="ACD-class-info-item">
                                            <i className="bi-play-circle ACD-class-info-icon" />
                                            <div className="ACD-class-info-content">
                                              <span className="ACD-class-info-label">Video URL</span>
                                              <span className="ACD-class-info-value">
                                                <a href={classItem.videoUrl || classItem.linkVideo} target="_blank" rel="noopener noreferrer" className="ACD-class-link">
                                                  {classItem.videoUrl || classItem.linkVideo}
                                                </a>
                                              </span>
                                            </div>
                                          </div>
                                        ) : null}
                                        <div className="ACD-class-info-item">
                                          <i className="bi-clock ACD-class-info-icon" />
                                          <div className="ACD-class-info-content">
                                            <span className="ACD-class-info-label">Duración</span>
                                            <span className="ACD-class-info-value">
                                              {minutesToDisplay(classItem.minutes ?? classItem.duracionMinutos ?? classItem.duracion ?? 0)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <button
                              className="ACD-empty-classes-button"
                              onClick={() => openClassModal(module)}
                            >
                              <i className="bi-plus-circle" />
                              Crear primera clase
                            </button>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Sección de Exámenes */}
          {hasEditExamsPermission && (
            <section className="ACD-section">
              <div className="ACD-section-header">
                <div className="ACD-section-header-content">
                  <h2 className="ACD-section-title">
                    <i className="bi-clipboard-check text-primary" />
                    Exámenes del curso
                  </h2>
                  <p className="ACD-section-subtitle">
                    Crea y gestiona exámenes para evaluar el conocimiento de los estudiantes.
                  </p>
                </div>
                <Button variant="primary" onClick={() => openExamModal()}>
                  <i className="bi-plus-circle me-2" />
                  Crear examen
                </Button>
              </div>

              {loadingExams ? (
                <div className="ACD-skeleton-module">
                  <Placeholder animation="wave">
                    <Placeholder xs={12} style={{ height: '60px', marginBottom: '1rem' }} />
                    <Placeholder xs={12} style={{ height: '60px' }} />
                  </Placeholder>
                </div>
              ) : exams.length === 0 ? (
                <div className="ACD-empty-state">
                  <i className="bi-clipboard-x" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }} />
                  <p className="text-muted">Este curso aún no tiene exámenes.</p>
                  <Button variant="primary" onClick={() => openExamModal()}>
                    <i className="bi-plus-circle me-2" />
                    Crear primer examen
                  </Button>
                </div>
              ) : (
                <div className="ACD-exams-list">
                  {exams.map((exam) => {
                    const isExpanded = expandedExams.has(exam.id);
                    const preguntas = exam.preguntas || [];
                    return (
                      <div key={exam.id} className="ACD-exam-card">
                        <div
                          className="ACD-exam-header"
                          onClick={() => toggleExam(exam.id)}
                        >
                          <div className="ACD-exam-header-content">
                            <i
                              className={`bi-chevron-${isExpanded ? 'down' : 'right'} ACD-chevron-icon`}
                            />
                            <div className="ACD-exam-title-section">
                              <h3 className="ACD-exam-title">{exam.titulo}</h3>
                              <div className="ACD-exam-meta">
                                <span className="ACD-exam-meta-item">
                                  <i className="bi-question-circle" />
                                  {preguntas.length} {preguntas.length === 1 ? 'pregunta' : 'preguntas'}
                                      </span>
                                <span className="ACD-exam-meta-item">
                                  <i className="bi-star-fill" />
                                  Nota mínima: {exam.notaMinima || 6.0}
                                      </span>
                                    </div>
                            </div>
                          </div>
                          <div className="ACD-exam-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                              onClick={() => openQuestionModal(exam)}
                              title="Agregar pregunta"
                            >
                              <i className="bi-plus-circle" />
                            </button>
                            <button
                              className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                              onClick={() => openExamModal(exam)}
                              title="Editar examen"
                            >
                              <i className="bi-pencil" />
                            </button>
                            <button
                              className="ACD-btn ACD-btn-outline-danger ACD-btn-sm"
                              onClick={() => handleDeleteExam(exam.id)}
                              title="Eliminar examen"
                            >
                              <i className="bi-trash" />
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="ACD-exam-content">
                            {exam.descripcion && (
                              <p className="ACD-exam-description">{exam.descripcion}</p>
                            )}
                            {preguntas.length === 0 ? (
                              <div className="ACD-empty-questions">
                                <p className="text-muted">Este examen aún no tiene preguntas.</p>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                  onClick={() => openQuestionModal(exam)}
                                      >
                                  <i className="bi-plus-circle me-2" />
                                  Agregar primera pregunta
                                      </Button>
                                    </div>
                            ) : (
                              <div className="ACD-questions-list">
                                {preguntas.map((pregunta) => {
                                  const isQuestionExpanded = expandedQuestions.has(pregunta.id);
                                  const respuestas = pregunta.respuestas || [];
                                  return (
                                    <div key={pregunta.id} className="ACD-question-card">
                                      <div
                                        className="ACD-question-header"
                                        onClick={() => toggleQuestion(pregunta.id)}
                                      >
                                        <div className="ACD-question-header-content">
                                          <i
                                            className={`bi-chevron-${isQuestionExpanded ? 'down' : 'right'} ACD-chevron-icon`}
                                          />
                                          <div className="ACD-question-title-section">
                                            <span className="ACD-question-order">Pregunta #{pregunta.orden}</span>
                                            <p className="ACD-question-text">{pregunta.pregunta}</p>
                                            <div className="ACD-question-meta">
                                              <Badge bg={pregunta.tipo === 'multiple' ? 'info' : 'primary'}>
                                                {pregunta.tipo === 'multiple' ? 'Múltiple opción' : 'Única opción'}
                                              </Badge>
                                              <span className="ACD-question-meta-item">
                                                {respuestas.length} {respuestas.length === 1 ? 'respuesta' : 'respuestas'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="ACD-question-actions" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                                            onClick={() => openAnswerModal(pregunta)}
                                            title="Agregar respuesta"
                                          >
                                            <i className="bi-plus-circle" />
                                          </button>
                                          <button
                                            className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                                            onClick={() => openQuestionModal(exam, pregunta)}
                                            title="Editar pregunta"
                                          >
                                            <i className="bi-pencil" />
                                          </button>
                                        </div>
                                      </div>
                                      {isQuestionExpanded && (
                                        <div className="ACD-question-content">
                                          {respuestas.length === 0 ? (
                                            <div className="ACD-empty-answers">
                                              <p className="text-muted">Esta pregunta aún no tiene respuestas.</p>
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openAnswerModal(pregunta)}
                                              >
                                                <i className="bi-plus-circle me-2" />
                                                Agregar primera respuesta
                                </Button>
                                            </div>
                                          ) : (
                                            <div className="ACD-answers-list">
                                              {respuestas
                                                .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                                                .map((respuesta) => (
                                                  <div
                                                    key={respuesta.id}
                                                    className={`ACD-answer-item ${respuesta.esCorrecta ? 'ACD-answer-correct' : ''}`}
                                                  >
                                                    <div className="ACD-answer-content">
                                                      <span className="ACD-answer-order">#{respuesta.orden}</span>
                                                      <span className="ACD-answer-text">{respuesta.texto}</span>
                                                      {respuesta.esCorrecta && (
                                                        <Badge bg="success" className="ACD-answer-badge">
                                                          <i className="bi-check-circle" />
                                                          Correcta
                                                        </Badge>
                          )}
                        </div>
                                                    <button
                                                      className="ACD-btn ACD-btn-outline-primary ACD-btn-sm"
                                                      onClick={() => openAnswerModal(pregunta, respuesta)}
                                                      title="Editar respuesta"
                                                    >
                                                      <i className="bi-pencil" />
                                                    </button>
                                                  </div>
                                                ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
            )}
          </section>
          )}
          </div>
        </main>
      </div>


      {/* Modal imagen */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Form onSubmit={handleImageUpload}>
          <Modal.Header closeButton>
            <Modal.Title>Cambiar imagen del curso</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Nueva imagen</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(event) => setImageFile(event.target.files?.[0] ?? null)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowImageModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={imageLoading || !imageFile}>
              {imageLoading ? <Spinner animation="border" size="sm" /> : 'Guardar imagen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal módulo */}
      <Modal show={showModuleModal} onHide={() => setShowModuleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{moduleDraft.id ? 'Editar módulo' : 'Crear módulo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título</Form.Label>
            <Form.Control
              value={moduleDraft.name}
              onChange={(event) =>
                setModuleDraft((prev) => ({
                  ...prev,
                  name: event.target.value,
                }))
              }
              maxLength={200}
              required
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={moduleDraft.shortDescription}
              onChange={(event) =>
                setModuleDraft((prev) => ({
                  ...prev,
                  shortDescription: event.target.value,
                }))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModuleModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveModule} disabled={moduleLoading}>
            {moduleLoading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal clase */}
      <Modal show={showClassModal} onHide={() => setShowClassModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{classDraft.id ? 'Editar clase' : 'Crear clase'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título</Form.Label>
            <Form.Control
              value={classDraft.title}
              onChange={(event) =>
                setClassDraft((prev) => ({
                  ...prev,
                  title: event.target.value,
                }))
              }
              maxLength={200}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción breve</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={classDraft.shortDescription}
              onChange={(event) =>
                setClassDraft((prev) => ({
                  ...prev,
                  shortDescription: event.target.value,
                }))
              }
              maxLength={300}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción extendida</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={classDraft.extendedDescription}
              onChange={(event) =>
                setClassDraft((prev) => ({
                  ...prev,
                  extendedDescription: event.target.value,
                }))
              }
              maxLength={2000}
            />
          </Form.Group>
          <div className="ACD-class-form-grid">
            <div className="ACD-class-form-group-full">
              <Form.Group>
                <Form.Label>URL del video</Form.Label>
                <Form.Control
                  value={classDraft.videoUrl}
                  onChange={(event) =>
                    setClassDraft((prev) => ({
                      ...prev,
                      videoUrl: event.target.value,
                    }))
                  }
                  placeholder="https://..."
                />
              </Form.Group>
            </div>
            <div className="ACD-class-form-group">
              <Form.Group>
                <Form.Label>Duración (min)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={classDraft.durationMinutes}
                  onChange={(event) =>
                    setClassDraft((prev) => ({
                      ...prev,
                      durationMinutes: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
            <div className="ACD-class-form-group">
              <Form.Group>
                <Form.Label>Orden</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={classDraft.orderIndex}
                  onChange={(event) =>
                    setClassDraft((prev) => ({
                      ...prev,
                      orderIndex: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mt-3">
            <Form.Label>Materiales (archivos PDF, imágenes, etc.)</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip"
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                setClassMaterials(files);
              }}
            />
            {classMaterials.length > 0 && (
              <Form.Text className="text-muted">
                {classMaterials.length} archivo(s) seleccionado(s)
              </Form.Text>
            )}
            <Form.Text className="text-muted d-block">
              Puedes subir múltiples archivos. Formatos permitidos: PDF, DOC, DOCX, imágenes, ZIP.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowClassModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveClass}
            disabled={classLoading || uploadingMaterials}
          >
            {classLoading || uploadingMaterials ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {uploadingMaterials ? 'Subiendo materiales...' : 'Guardando...'}
              </>
            ) : (
              'Guardar clase'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal examen */}
      <Modal show={showExamModal} onHide={() => { setShowExamModal(false); resetExamDraft(); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{examDraft.id ? 'Editar examen' : 'Crear examen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título del examen *</Form.Label>
            <Form.Control
              type="text"
              value={examDraft.titulo}
              onChange={(e) => setExamDraft({ ...examDraft, titulo: e.target.value })}
              placeholder="Ej: Examen Final del Curso"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={examDraft.descripcion}
              onChange={(e) => setExamDraft({ ...examDraft, descripcion: e.target.value })}
              placeholder="Descripción del examen (opcional)"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Nota mínima para aprobar *</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={examDraft.notaMinima}
              onChange={(e) => setExamDraft({ ...examDraft, notaMinima: parseFloat(e.target.value) || 6.0 })}
              required
            />
            <Form.Text className="text-muted">Nota mínima requerida para aprobar el examen (0-10)</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setShowExamModal(false); resetExamDraft(); }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveExam} disabled={examLoading || !examDraft.titulo.trim()}>
            {examLoading ? <Spinner animation="border" size="sm" /> : examDraft.id ? 'Actualizar' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal pregunta */}
      <Modal show={showQuestionModal} onHide={() => { setShowQuestionModal(false); resetQuestionDraft(); setSelectedExam(null); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{questionDraft.id ? 'Editar pregunta' : 'Crear pregunta'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Pregunta *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={questionDraft.pregunta}
              onChange={(e) => setQuestionDraft({ ...questionDraft, pregunta: e.target.value })}
              placeholder="Escribe la pregunta aquí..."
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de pregunta *</Form.Label>
            <Form.Select
              value={questionDraft.tipo}
              onChange={(e) => setQuestionDraft({ ...questionDraft, tipo: e.target.value })}
            >
              <option value="unica">Única opción (solo una respuesta correcta)</option>
              <option value="multiple">Múltiple opción (varias respuestas correctas)</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setShowQuestionModal(false); resetQuestionDraft(); setSelectedExam(null); }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveQuestion} disabled={questionLoading || !questionDraft.pregunta.trim()}>
            {questionLoading ? <Spinner animation="border" size="sm" /> : questionDraft.id ? 'Actualizar' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal respuesta */}
      <Modal show={showAnswerModal} onHide={() => { setShowAnswerModal(false); resetAnswerDraft(); setSelectedQuestion(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>{answerDraft.id ? 'Editar respuesta' : 'Crear respuesta'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Texto de la respuesta *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={answerDraft.texto}
              onChange={(e) => setAnswerDraft({ ...answerDraft, texto: e.target.value })}
              placeholder="Escribe la respuesta aquí..."
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Esta es la respuesta correcta"
              checked={answerDraft.esCorrecta}
              onChange={(e) => setAnswerDraft({ ...answerDraft, esCorrecta: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setShowAnswerModal(false); resetAnswerDraft(); setSelectedQuestion(null); }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveAnswer} disabled={answerLoading || !answerDraft.texto.trim()}>
            {answerLoading ? <Spinner animation="border" size="sm" /> : answerDraft.id ? 'Actualizar' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminCourseDetailPage = () => (
  <AdminNotificationsProvider>
    <AdminCourseDetailInner />
  </AdminNotificationsProvider>
);

export default AdminCourseDetailPage;

